import React, { useCallback, useEffect } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ModalTransition } from '@atlaskit/modal-dialog';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { UI_EVENT_TYPE } from '../../common/constants.tsx';
import { useCommandPalette } from '../../controllers/command-palette/index.tsx';
import { MODAL_DIALOG_TEST_ID } from './constants.tsx';
import { messages } from './messages.tsx';
import { ModalContent } from './modal-content/index.tsx';
import { ModalWrapper } from './modal-wrapper/index.tsx';
import type { CommandPaletteModalProps } from './types.tsx';

export const CommandPaletteModal = ({
	defaultAriaLabel,
	defaultPlaceholder,
	defaultFooterContent,
	onAnalytics,
	onErrorAnalytics,
	defaultFallback,
	rootFallback,
}: CommandPaletteModalProps): React.JSX.Element | null => {
	const [
		{ commandPaletteSessionId, isOpen, hasPopupOverlay },
		{ close, markAsLoaded, setAnalytics },
	] = useCommandPalette();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useEffect(() => {
		setAnalytics(onAnalytics, onErrorAnalytics);
	}, [onAnalytics, onErrorAnalytics, setAnalytics]);

	const sendAnalyticsEvent = useCallback(
		(action: string): void => {
			const ev = createAnalyticsEvent({
				action,
				actionSubject: 'commandPalette',
			});
			if (onAnalytics) {
				onAnalytics(ev, UI_EVENT_TYPE, {
					attributes: { commandPaletteSessionId },
				});
			}
		},
		[commandPaletteSessionId, createAnalyticsEvent, onAnalytics],
	);

	const onEscPressed = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape' && isOpen) {
				event.preventDefault();
				event.stopPropagation();
				sendAnalyticsEvent('closed');
				traceUFOPress('command-palette-closed');
				close();
			}
		},
		[isOpen, sendAnalyticsEvent, close],
	);

	useEffect(() => {
		if (hasPopupOverlay) return;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('keydown', onEscPressed, true);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('keydown', onEscPressed, true);
		};
	}, [hasPopupOverlay, onEscPressed]);

	useEffect(() => {
		markAsLoaded();
	}, [markAsLoaded]);

	const onClose = useCallback(() => {
		sendAnalyticsEvent('closed');
		traceUFOPress('command-palette-closed');
		close();
	}, [sendAnalyticsEvent, close]);

	const onOpenComplete = useCallback(() => {
		sendAnalyticsEvent('viewed');
	}, [sendAnalyticsEvent]);

	if (!isOpen) return null;

	return (
		<ModalTransition>
			<ModalDialog
				onClose={onClose}
				shouldCloseOnOverlayClick
				shouldCloseOnEscapePress
				autoFocus={false}
				testId={MODAL_DIALOG_TEST_ID}
				onOpenComplete={onOpenComplete}
				width={752}
				isBlanketHidden
				label={formatMessage(messages.modalLabel)}
				messageId="command-palette.ui.modal.modal-dialog"
				messageType="transactional"
			>
				<ModalWrapper>
					<ModalContent
						defaultAriaLabel={defaultAriaLabel}
						defaultFallback={defaultFallback}
						rootFallback={rootFallback}
						defaultPlaceholder={defaultPlaceholder}
						defaultFooterContent={defaultFooterContent}
						onClose={onClose}
					/>
				</ModalWrapper>
			</ModalDialog>
		</ModalTransition>
	);
};
